import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

// Store
import { EsignatureActions, EsignatureSelectors } from '@amfam/policy/e-signature/data-access';
import {
  GoPaperLessActions,
  GoPaperLessSelectors,
  GoPaperlessService
} from '@amfam/policy/go-paperless/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService,
  ValidationService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { GoPaperlessComponentDirective } from '../shared';

@Component({
  selector: 'app-enroll-go-paperless',
  templateUrl: './enroll-go-paperless.component.html',
  styleUrls: ['./enroll-go-paperless.component.scss'],
  standalone: false
})
export class EnrollGoPaperlessComponent
  extends GoPaperlessComponentDirective
  implements OnInit, OnDestroy
{
  @Input() goPaperLessSignatureError = false;

  public enrollPaperlessForm: UntypedFormGroup;
  public requiresSignature: Observable<boolean>;
  public errorList: Array<ErrorObj> = [];
  public disableEnrollBtn = false;
  // Shared subject for completing observables
  private done$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private goPaperlessService: GoPaperlessService,
    private pageTitle: PageTitleService,
    private utilService: UtilService,
    public spinner: LoadingSpinnerService,
    store: Store<fromRoot.RootState>,
    analyticsFacade: AnalyticsFacade,
    public roleService: ImpersonateRolesService,
    private copyService: CopyService,
    private dockingbarService: DockingBarService
  ) {
    super(store, analyticsFacade);
    this.spinner.start();
  }

  enroll() {
    this.disableEnrollBtn = true;
    this.store
      .select(EsignatureSelectors.selectESignatureRequired)
      .pipe(takeUntil(this.done$))
      .subscribe(requiresSignature => {
        if (requiresSignature && !this.goPaperLessSignatureError) {
          this.spinner.start();

          this.store
            .pipe(select(BrandSelectors.selectExperienceId), take(1))
            .subscribe(experienceId => {
              const callbackUrl = this.utilService.getAbsoluteUrl(
                'policies/gopaperless/signature',
                experienceId
              );
              const signatureRequest = this.goPaperlessService.createSignatureRequest(callbackUrl);
              this.store.dispatch(EsignatureActions.SignESignature(signatureRequest));
            });
        } else {
          this.store.dispatch(GoPaperLessActions.EnrollGoPaperless());
          combineLatest(
            this.store.select(GoPaperLessSelectors.selectGoPaperlessLoading),
            this.store.select(GoPaperLessSelectors.selectGoPaperlessError),
            (loading, hasError) => {
              return {
                hasError: hasError,
                loading: loading
              };
            }
          )
            .pipe(
              takeUntil(this.done$),
              filter(state => !state.loading)
            )
            .subscribe(state => {
              if (state.hasError) {
                this.analyticsFacade.trackPageAndError(this.errorAnalytic, 'EnrollError');
                this.errorList = this.showErrorMessage([
                  this.copyService.get('policy', 'defaultErrorMessage')
                ]);
                this.disableEnrollBtn = false;
              } else {
                this.store.dispatch(
                  fromRouterActions.Go({
                    path: ['/policies/gopaperless/confirmation']
                  })
                );
              }
            });
        }
      });
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageTitle.set('Sign Up for Paperless Policy Documents');

    this.enrollPaperlessForm = this.formBuilder.group({
      agreeToTerms: [false, ValidationService.isChecked]
    });

    this.requiresSignature = this.store.select(EsignatureSelectors.selectESignatureRequired);
    if (this.goPaperLessSignatureError) {
      this.errorList = this.showErrorMessage([
        this.copyService.get('policy', 'defaultErrorMessage')
      ]);
      this.enrollPaperlessForm.controls['agreeToTerms'].setValue(true);
      this.dockingbarService.registerHeading('Sign Up for Paperless Policy Documents');
    }

    this.spinner.stop();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.done$.next();
    this.done$.complete();
  }

  cancel(): void {
    this.store.dispatch(fromRouterActions.Back());
  }

  private showErrorMessage(
    errorMessageList: Array<String>,
    errorLevel?: ErrorLevels
  ): Array<ErrorObj> {
    let errorList: Array<ErrorObj> = [];
    errorMessageList.forEach((msg: string) => {
      if (!errorList.find(error => error.errorMessage === msg)) {
        errorList = [
          ...errorList,
          { errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH }
        ];
      }
    });
    return errorList;
  }
}
